<template>
  <ContentWrap>
    <el-form :inline="true" :model="query" class="-mb-15px">
      <el-form-item label="认证类型">
        <el-select v-model="query.certType" clearable class="!w-140px" placeholder="全部">
          <el-option label="个人认证" :value="1" />
          <el-option label="企业认证" :value="2" />
        </el-select>
      </el-form-item>
      <el-form-item label="会员">
        <el-input
          v-model="query.keyword"
          clearable
          class="!w-220px"
          placeholder="姓名/昵称/手机/用户ID"
          @keyup.enter="search"
        />
      </el-form-item>
      <el-form-item label="支付状态">
        <el-select v-model="query.payStatus" clearable class="!w-140px" placeholder="全部">
          <el-option label="免费/未付" :value="0" />
          <el-option label="待支付" :value="1" />
          <el-option label="已支付" :value="2" />
          <el-option label="已关闭" :value="3" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="search">搜索</el-button>
        <el-button @click="reset">重置</el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-alert
      class="mb-12px"
      type="info"
      :closable="false"
      show-icon
      title="展示个人/企业认证费统一订单（type=28）。价格在「运营配置」配置；免费模式不会产生本列表订单。"
    />
    <el-table v-loading="loading" :data="list" stripe>
      <el-table-column prop="certId" label="申请ID" width="90" />
      <el-table-column prop="tradeOrderId" label="统一订单ID" width="120" />
      <el-table-column prop="tradeOrderNo" label="订单号" min-width="170" show-overflow-tooltip />
      <el-table-column label="类型" width="110">
        <template #default="{ row }">
          <el-tag v-if="row.certType === 1" type="primary">个人认证</el-tag>
          <el-tag v-else-if="row.certType === 2" type="warning">企业认证</el-tag>
          <span v-else>{{ row.title || '-' }}</span>
        </template>
      </el-table-column>
      <el-table-column label="会员资料" min-width="240">
        <template #default="{ row }">
          <div class="flex items-center gap-10px">
            <el-avatar :size="40" :src="row.avatar || undefined">
              <Icon icon="ep:user-filled" />
            </el-avatar>
            <div class="min-w-0">
              <div class="font-500 truncate">
                {{ row.realName || row.userNickname || `用户${row.userId}` }}
              </div>
              <div class="text-12px text-#909399 truncate">
                昵称 {{ row.userNickname || '-' }}
                <span v-if="row.realName"> · 姓名 {{ row.realName }}</span>
              </div>
              <div class="text-12px text-#909399">
                {{ row.mobile || '无手机号' }} · ID {{ row.userId }}
              </div>
            </div>
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="subjectName" label="认证主体" min-width="140" show-overflow-tooltip />
      <el-table-column label="金额(元)" width="100">
        <template #default="{ row }">{{ formatAmount(row.amount) }}</template>
      </el-table-column>
      <el-table-column label="支付" width="100">
        <template #default="{ row }">
          <el-tag :type="payTag(row.payStatus)">{{ payText(row.payStatus) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="交易状态" width="100">
        <template #default="{ row }">{{ tradeStatusText(row.tradeStatus) }}</template>
      </el-table-column>
      <el-table-column prop="payOrderId" label="支付单" width="110" />
      <el-table-column prop="paidTime" label="支付时间" width="170" :formatter="dateFormatter" />
      <el-table-column prop="createTime" label="创建时间" width="170" :formatter="dateFormatter" />
    </el-table>
    <Pagination
      v-model:page="query.pageNo"
      v-model:limit="query.pageSize"
      :total="total"
      @pagination="load"
    />
  </ContentWrap>
</template>

<script setup lang="ts">
import { dateFormatter } from '@/utils/formatTime'
import * as Api from '@/api/business/certOrder'

defineOptions({ name: 'BossCircleCertOrder' })

const loading = ref(false)
const list = ref<Api.CertOrderVO[]>([])
const total = ref(0)
const query = reactive({
  pageNo: 1,
  pageSize: 10,
  certType: undefined as number | undefined,
  keyword: '',
  payStatus: undefined as number | undefined
})

const formatAmount = (v?: number) => (v == null ? '0.00' : Number(v).toFixed(2))
const payText = (s?: number) =>
  ({ 0: '免费/未付', 1: '待支付', 2: '已支付', 3: '已关闭' } as Record<number, string>)[s ?? -1] ||
  '-'
const payTag = (s?: number) =>
  s === 2 ? 'success' : s === 1 ? 'warning' : s === 3 ? 'info' : ''
const tradeStatusText = (s?: number) =>
  ({ 0: '待支付', 10: '待发货', 20: '已发货', 30: '已完成', 40: '已取消' } as Record<
    number,
    string
  >)[s ?? -1] || (s == null ? '-' : String(s))

const load = async () => {
  loading.value = true
  try {
    const params: any = {
      pageNo: query.pageNo,
      pageSize: query.pageSize,
      certType: query.certType,
      payStatus: query.payStatus
    }
    if (query.keyword?.trim()) params.keyword = query.keyword.trim()
    const data = await Api.getCertOrderPage(params)
    list.value = data.list || []
    total.value = data.total || 0
  } finally {
    loading.value = false
  }
}

const search = () => {
  query.pageNo = 1
  load()
}

const reset = () => {
  query.certType = undefined
  query.keyword = ''
  query.payStatus = undefined
  search()
}

onMounted(load)
</script>
